import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, Form, Input, Button, Typography } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/stores/auth.store';

const { Title, Text } = Typography;

interface LoginForm {
  username: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (values: LoginForm) => {
    setLoading(true);
    setError(null);
    try {
      await login(values.username, values.password);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '登录失败，请检查管理员账号和密码。');
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return null;
  }

  return (
    <main className="admin-login-page">
      <section className="admin-login-story" aria-label="猫知岁管理工作台">
        <span className="admin-login-brand">
          猫知岁 <small>MEOWMOIR</small>
        </span>
        <div className="admin-login-story-copy">
          <span>为每一份日常照护</span>
          <h1>
            把细微的记录，
            <br />
            汇成更好的关爱。
          </h1>
          <p>
            连接用户、猫咪和照护数据。
            <br />
            让每一次反馈有回应，每一份资料更可靠。
          </p>
        </div>
        <span className="admin-login-footnote">用户与照护 · 内容与审核 · 健康与 AI</span>
      </section>
      <div className="admin-login-form-area">
        <Card className="admin-login-card" variant="borderless">
          <div className="admin-login-heading">
            <Text className="admin-workspace-eyebrow">MEOWMOIR ADMIN</Text>
            <Title level={2}>登录管理工作台</Title>
            <Text type="secondary">使用管理员账号，继续今天的工作。</Text>
          </div>
          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 20 }} />}
          <Form<LoginForm> name="login" onFinish={handleSubmit} size="large" layout="vertical">
            <Form.Item
              name="username"
              label="管理员账号"
              rules={[{ required: true, message: '请输入管理员账号' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="请输入管理员账号"
                autoComplete="username"
              />
            </Form.Item>
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="请输入密码"
                autoComplete="current-password"
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={loading} block>
                登录工作台
              </Button>
            </Form.Item>
          </Form>
        </Card>
        <p className="admin-login-support">猫知岁 · 管理员专用入口</p>
      </div>
    </main>
  );
};

export default LoginPage;
